import {
  convertUnicodeString,
  replaceUnderscoreWithSpace,
} from '~/views/private/overview/utils';
import { getLocationName } from '~/utils/location';

export function normalizeSearchText(text) {
  if (text == null || text === '') {
    return '';
  }

  let normalized = convertUnicodeString(String(text));

  try {
    normalized = decodeURIComponent(normalized);
  } catch {
    // Keep the partially decoded value.
  }

  normalized = convertUnicodeString(normalized);
  normalized = replaceUnderscoreWithSpace(normalized);

  return normalized
    .replace(/\+/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function textMatchesSearch(text, search) {
  const normalizedSearch = normalizeSearchText(search);

  if (!normalizedSearch) {
    return true;
  }

  const normalizedText = normalizeSearchText(text);

  if (normalizedText.includes(normalizedSearch)) {
    return true;
  }

  const rawText = String(text).toLowerCase();
  const encodedSearch = normalizedSearch.replace(/ /g, '%20');

  return encodedSearch !== normalizedSearch && rawText.includes(encodedSearch);
}

export function hasDisplayableTour({ locations, explorers3D }) {
  return locations?.length > 0 || explorers3D?.length > 0;
}

export function groupDataByCompany(dataArray) {
  return dataArray.reduce((acc, item) => {
    const companyIndex = acc.findIndex(
      entry => entry.company_id === item.company_id,
    );

    const property = {
      property_id: item.property_id,
      property_name: item.property_name,
      locations: item.locations,
      explorers3D: item.explorers3D,
    };

    if (companyIndex !== -1) {
      acc[companyIndex].properties.push(property);
    } else {
      acc.push({
        companyName: item.companyName,
        company_id: item.company_id,
        properties: [property],
      });
    }

    return acc;
  }, []);
}

export function searchClient(text, properties, options = {}) {
  const locationsSet = options.locationsSet || new Set();
  const explorers3DSet = options.explorers3DSet || new Set();
  const propertiesSet = options.propertiesSet || new Set();

  let propertiesCount = 0;

  for (const property of properties) {
    const { locations, explorers3D, property_name, property_id } = property;

    if (!hasDisplayableTour(property)) {
      continue;
    }

    if (textMatchesSearch(property_name, text)) {
      propertiesSet.add(property_id);
      propertiesCount++;
    }

    for (const l of locations || []) {
      const locationName = getLocationName(l);

      if (textMatchesSearch(locationName, text)) {
        locationsSet.add(locationName);
        propertiesSet.add(property_id);
        propertiesCount++;
      }
    }

    for (const e of explorers3D || []) {
      if (textMatchesSearch(e.name, text)) {
        explorers3DSet.add(e.name);
        propertiesSet.add(property_id);
        propertiesCount++;
      }
    }
  }

  return {
    locations: locationsSet,
    explorers3D: explorers3DSet,
    properties: propertiesSet,
    propertiesCount,
  };
}

export function searchAdmin(text, companies) {
  const companiesSet = new Set();
  const propertiesSet = new Set();
  const locationsSet = new Set();
  const explorers3DSet = new Set();

  for (const { companyName, properties } of companies) {
    const displayableProperties = properties.filter(hasDisplayableTour);

    if (displayableProperties.length === 0) {
      continue;
    }

    const searchResult = searchClient(text, properties, {
      locationsSet,
      explorers3DSet,
      propertiesSet,
    });

    if (textMatchesSearch(companyName, text)) {
      companiesSet.add(companyName);
      for (const { property_id } of displayableProperties) {
        propertiesSet.add(property_id);
      }
    } else if (searchResult.propertiesCount > 0) {
      companiesSet.add(companyName);
    }
  }

  return {
    companies: companiesSet,
    properties: propertiesSet,
    locations: locationsSet,
    explorers3D: explorers3DSet,
  };
}

export function filterGalleryProperties(stats, search) {
  if (!stats?.length) {
    return [];
  }

  const displayableStats = stats.filter(hasDisplayableTour);

  if (!search) {
    return displayableStats;
  }

  const searchResult = searchClient(search, stats);

  return displayableStats.filter(({ property_id }) =>
    searchResult.properties.has(property_id),
  );
}
